import { useCallback, useMemo, useRef, useState } from "react";
// Si ya tienes este helper en tu proyecto, úsalo. Si no, este hook es autónomo.
import { obtenerRegistrosPaginados } from "../../../services/api";
/**
 * Hook de búsqueda y paginación para AdminOperativo
 *
 * Corrige la deserialización del response del backend (message + data { data, total, ... })
 * y normaliza al shape que la UI espera: { records, totalRecords, currentPage, totalPages, users? }
 *
 * API esperada en backend: obtenerRegistrosPaginados(filtros, pageSize, pagina)
 * Devuelve: { message: 'OK', data: { data: [...], total: number, currentPage?, totalPages?, users? } }
 */
export default function useSearch(initialPageSize = 20) {
  // ---- Estado base ----
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [searchData, setSearchData] = useState({
    records: [],
    totalRecords: 0,
    currentPage: 1,
    totalPages: 0,
    users: [],
  });

  // Guardamos el último query y filtros para paginación
  const lastQueryRef = useRef("");
  const lastExtraFiltersRef = useRef({});

  // ---- Helpers de mapeo/normalización ----
  const mapMetricToEstado = useCallback((metric) => {
    // CORRECCIÓN: Mapeo actualizado según MetricsBar.js
    const map = {
      activos: "activo",
      inactivos: "inactivo", 
      pendientePorLigar: "pendientePorLigar",
      impresos: "impreso",
      entregados: "entregado",
      perdidos: "perdido",
      porVencer: "porVencer",
      vencidos: "vencido"
    };
    return map[metric] || undefined;
  }, []);

  const inferFiltersFromQuery = useCallback((query) => {
    // Heurística ligera: si hay "@" parece correo; si es numérico largo, parece folio
    const q = (query || "").trim();
    const maybe = {};
    if (!q) return maybe;

    if (/@/.test(q)) maybe.correo = q;
    else if (/^\d+$/.test(q)) maybe.folio = q; // cualquier número → folio
    else maybe.texto = q; // backend podría buscar por texto libre

    return maybe;
  }, []);

  const buildBackendFilters = useCallback((query, extraFilters = {}) => {
  const base = inferFiltersFromQuery(query);

  // Manejar filtros de métricas especiales
  if (extraFilters.metricFilter) {
    // NO mapear "perdidos" a estado, manejarlo como campo especial
    if (extraFilters.metricFilter === 'perdidos') {
      base.extraviado = true; // Campo booleano directo
    } else if (extraFilters.metricFilter === 'porVencer') {
      base.porVencer = true;
    } else if (extraFilters.metricFilter === 'vencidos') {
      base.vencidos = true;
    } else {
      // Para los demás, mapear a estado
      const estado = mapMetricToEstado(extraFilters.metricFilter);
      if (estado) base.estado = estado;
    }
  }

  // Merge explícito de otros filtros
  const passthroughKeys = [
    "tipo",
    "correo",
    "estado",
    "plan",
    "folio",
    "fechaInicio",
    "fechaFin",
    "texto",
    "estadoImpresion",
    "loteImpresion",
    "incluirOcultos",
  ];
  for (const key of passthroughKeys) {
    if (extraFilters[key] !== undefined && extraFilters[key] !== null && extraFilters[key] !== "") {
      base[key] = extraFilters[key];
    }
  }

  return base;
}, [inferFiltersFromQuery, mapMetricToEstado]);

  const normalizePayload = useCallback((resp, fallbackPage, fallbackPageSize) => {
    // CORRECCIÓN PRINCIPAL: Manejo correcto de la estructura del backend
    // El backend devuelve: { message: 'OK', data: result }
    // donde result puede ser:
    // 1) { data: [...], total: number, totalPages: number, currentPage: number, users: [...] }
    // 2) Directamente un array de registros
    // 3) { registros: [...], total: number }
    
    let records = [];
    let totalRecords = 0;
    let currentPage = fallbackPage || 1;
    let totalPages = 0;
    let users = [];

    // Si existe resp.data (viene del backend con { message, data })
    if (resp?.data) {
      const payload = resp.data;

      // Si payload.data existe (estructura anidada)
      if (payload?.data) {
        records = Array.isArray(payload.data) ? payload.data : [];
        totalRecords = payload.total || records.length;
        currentPage = payload.currentPage || fallbackPage || 1;
        totalPages = payload.totalPages || Math.ceil(totalRecords / (fallbackPageSize || 20));
        users = payload.users || [];
      }
      // Si payload es directamente un array
      else if (Array.isArray(payload)) {
        records = payload;
        totalRecords = records.length;
        totalPages = Math.ceil(totalRecords / (fallbackPageSize || 20));
      }
      // Si payload tiene estructura { registros: [...] }
      else if (payload?.registros) {
        records = Array.isArray(payload.registros) ? payload.registros : [];
        totalRecords = payload.total || records.length;
        totalPages = payload.totalPages || Math.ceil(totalRecords / (fallbackPageSize || 20));
        users = payload.users || [];
      }
      // Si payload tiene otras propiedades que podrían ser los registros
      else if (typeof payload === 'object') {
        // Intentar encontrar un array en las propiedades
        const possibleArrayKeys = ['records', 'items', 'results', 'rows'];
        for (const key of possibleArrayKeys) {
          if (Array.isArray(payload[key])) {
            records = payload[key];
            totalRecords = payload.total || payload.totalRecords || records.length;
            totalPages = payload.totalPages || Math.ceil(totalRecords / (fallbackPageSize || 20));
            users = payload.users || [];
            break;
          }
        }
      }
    }
    // Si no hay resp.data, tal vez la respuesta es directamente los datos
    else if (Array.isArray(resp)) {
      records = resp;
      totalRecords = records.length;
      totalPages = Math.ceil(totalRecords / (fallbackPageSize || 20));
    }

    return {
      records: records,
      totalRecords: Number.isFinite(totalRecords) ? totalRecords : 0,
      currentPage: Number.isFinite(currentPage) ? currentPage : 1,
      totalPages: Number.isFinite(totalPages) ? totalPages : 0,
      users: users,
    };
  }, []);

  // ---- Acción principal de búsqueda ----
  const performSearch = useCallback(
    async (query = "", extraFilters = {}, explicitPage, explicitPageSize) => {
      setLoading(true);
      setError(null);

      // Si nos dieron page/pageSize explícitos, úsalo; si no, usamos estado
      const nextPage = explicitPage ?? page;
      const nextPageSize = explicitPageSize ?? pageSize;

      // Construimos filtros para backend
      const filtros = buildBackendFilters(query, extraFilters);

      try {
        // IMPORTANTE: la firma del backend es (filtros, pageSize, pagina)
        const resp = await obtenerRegistrosPaginados(filtros, nextPageSize, nextPage);

        const normalized = normalizePayload(resp, nextPage, nextPageSize);
        setSearchData(normalized);

        // Guardamos últimos criterios para navegar la paginación
        lastQueryRef.current = query;
        lastExtraFiltersRef.current = extraFilters;

        // Si el caller cambió la página explícitamente, sincronizamos el estado local
        if (explicitPage && explicitPage !== page) setPage(explicitPage);
        if (explicitPageSize && explicitPageSize !== pageSize) setPageSize(explicitPageSize);
      } catch (err) {
        console.error("performSearch error:", err);
        setError(err);
        // En caso de error, limpiamos para no dejar UI en estado inconsistente
        setSearchData((prev) => ({ ...prev, records: [], totalRecords: 0 }));
      } finally {
        setLoading(false);
      }
    },
    [buildBackendFilters, normalizePayload, page, pageSize]
  );

  // ---- Acciones auxiliares ----
  const refresh = useCallback(async () => {
    // Repite la última búsqueda con estado actual de paginación
    return performSearch(lastQueryRef.current, lastExtraFiltersRef.current, page, pageSize);
  }, [page, pageSize, performSearch]);

  const goToPage = useCallback(
    async (newPage) => {
      // mueve paginación y relanza
      setPage(newPage);
      return performSearch(lastQueryRef.current, lastExtraFiltersRef.current, newPage, pageSize);
    },
    [pageSize, performSearch]
  );

  const changePageSize = useCallback(
    async (newPageSize) => {
      // cambia pageSize, resetea a página 1 para evitar "huecos" y relanza
      setPageSize(newPageSize);
      setPage(1);
      return performSearch(lastQueryRef.current, lastExtraFiltersRef.current, 1, newPageSize);
    },
    [performSearch]
  );

  const resetResults = useCallback(() => {
    setSearchData({ records: [], totalRecords: 0, currentPage: 1, totalPages: 0, users: [] });
    setPage(1);
    setError(null);
  }, []);

  // ---- Derivados útiles ----
  const hasResults = useMemo(() => (searchData?.records?.length || 0) > 0, [searchData]);

  return {
    // estado
    loading,
    error,
    page,
    pageSize,
    searchData, // { records, totalRecords, currentPage, totalPages, users? }
    hasResults,

    // acciones
    performSearch, // (query, extraFilters, explicitPage?, explicitPageSize?)
    refresh,
    goToPage,
    changePageSize,
    resetResults,

    // utilidades para componer filtros desde UI externa si lo prefieres
    buildBackendFilters,
  };
}